import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { getSessionId } from '../lib/utils';
import { ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const { language } = useLanguage();
  const [faqItems, setFaqItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    // Log visitor
    axios.post(`${API}/visitor/log`, {
      page: 'faq',
      session_id: getSessionId(),
      user_agent: navigator.userAgent,
      referrer: document.referrer
    }).catch(() => {});

    // Fetch FAQ
    const fetchFAQ = async () => {
      try {
        const response = await axios.get(`${API}/faq`);
        setFaqItems(response.data.items || []);
      } catch (error) {
        // Error handled silently
      } finally {
        setLoading(false);
      }
    };

    fetchFAQ();
  }, []);

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 className="font-heading font-black text-4xl md:text-5xl uppercase tracking-tight">
            {language === 'fr' ? 'Questions Fréquentes' : 'FAQ'}
          </h1>
          <p className="font-body text-muted-foreground mt-4">
            {language === 'fr' 
              ? 'Retrouvez ici les réponses aux questions les plus fréquemment posées.'
              : 'Find answers to the most frequently asked questions here.'}
          </p>
        </motion.div>

        {faqItems.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-16"
          >
            <p className="font-body text-muted-foreground">
              {language === 'fr' 
                ? 'Aucune question fréquente pour le moment.'
                : 'No FAQ items available at the moment.'}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.div
                key={item.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-border"
                data-testid={`faq-item-${item.id || index}`}
              >
                <button
                  onClick={() => toggleItem(item.id || index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-secondary/50 transition-colors"
                  data-testid={`faq-toggle-${item.id || index}`}
                >
                  <h3 className="font-heading font-bold text-lg pr-4">
                    {language === 'fr' ? item.question_fr : item.question_en}
                  </h3>
                  <motion.div
                    animate={{ rotate: openItems[item.id || index] ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className="w-5 h-5" />
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {openItems[item.id || index] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-border pt-4">
                        <p className="font-body text-muted-foreground whitespace-pre-line">
                          {language === 'fr' ? item.answer_fr : item.answer_en}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
